import {
  Namespace,
  TFunction,
  useTranslation,
  UseTranslationOptions,
} from 'react-i18next'
import { useCallback } from 'react'
import i18n from 'i18n'

export const getTranslationList = (list: string[], path: string) => {
  const result = {} as any
  list.map((key) => {
    result[key] = i18n.t(`${path}.${key}`)
  })
  return result
}

interface TranslationRankmiProps {
  ns?: Namespace
  prefix?: string
  optionsTranslation?: UseTranslationOptions
}

export const joinKeys = (...keys: (string | undefined | null)[]) =>
  keys.filter((key) => !!key).join('.')

export const useTranslationRankmi = <N extends Namespace>({
  ns,
  prefix,
}: TranslationRankmiProps): TFunction<N> => {
  const { t } = useTranslation(ns)
  const translation = useCallback(
    (key, options) => t(joinKeys(prefix, key) as any, options),
    [prefix, t]
  )
  return translation
}

export interface Locate {
  header: Header
  controls: Controls
  table: Table
  filters: any
}

export interface LocatePartial {
  header?: Partial<Header>
  controls?: Partial<Controls>
  table?: Partial<Table>
}

interface Controls {
  rowsPerPage: string
}

interface Header {
  rowsCount: string
  rowsCount_plural: string
  selectedRows: string
  selectedRows_plural: string
  selectAll: string
  clearSelection: string
  search: string
  actions: string
  allParticipants: string
  addButton: string
}

interface Table {
  emptyMessage: string
}
interface GenerateTableLocaleArgs extends LocatePartial {
  paths?: {
    header?: string
    controls?: string
    table?: string
  }
}

export const generateTableLocale = (args?: GenerateTableLocaleArgs): Locate => {
  const { paths, header, controls, table } = { ...args }
  const tablePrefix = 'administration.courses.list'
  const locale: Locate = {
    header: {
      ...getTranslationList(
        [
          'rowsCount',
          'rowsCount_plural',
          'selectedRows',
          'selectedRows_plural',
          'selectAll',
          'clearSelection',
          'search',
          'actions',
          'allParticipants',
        ],
        paths?.header || `${tablePrefix}.table.header`
      ),
      ...header,
    },
    controls: {
      ...getTranslationList(
        ['rowsPerPage'],
        paths?.controls || `${tablePrefix}.table.controls`
      ),
      ...controls,
    },
    table: {
      ...getTranslationList(
        ['emptyMessage'],
        paths?.table || `${tablePrefix}.table.table`
      ),
      ...table,
    },
    filters: generateFilterViewerLocale(),
  }
  return locale
}

export const generateFilterViewerLocale = () => {
  const tablePrefix = joinKeys('administration.courses', 'list')

  let locale = {
    simpleFilter: { allConditions: {}, anyCondition: {} },
    advanceFilter: { allConditions: {}, anyCondition: {} },
    operations: {
      list: {},
      tagsList: {},
      string: {},
      number: {},
      date: {},
      boolean: {},
    },
    groupConditions: {},
    activeFilterViewer: { simple: {} },
  } as any

  locale = getTranslationList(
    [
      'filter',
      'header',
      'addFilter',
      'addFilterGroup',
      'simpleFilters',
      'advanceFilters',
      'today',
      'day',
      'days',
      'inputPlaceholder',
    ],
    `${tablePrefix}.table.filters`
  )
  locale.simpleFilter = getTranslationList(
    ['condition'],
    `${tablePrefix}.table.filters.simpleFilter`
  )
  locale.simpleFilter.allConditions = getTranslationList(
    ['title', 'description'],
    `${tablePrefix}.table.filters.simpleFilter.allConditions`
  )
  locale.simpleFilter.anyCondition = getTranslationList(
    ['title', 'description'],
    `${tablePrefix}.table.filters.simpleFilter.anyCondition`
  )
  locale.advanceFilter = getTranslationList(
    ['condition'],
    `${tablePrefix}.table.filters.advanceFilter`
  )
  locale.advanceFilter.allConditions = getTranslationList(
    ['title', 'description'],
    `${tablePrefix}.table.filters.advanceFilter.allConditions`
  )
  locale.advanceFilter.anyCondition = getTranslationList(
    ['title', 'description'],
    `${tablePrefix}.table.filters.advanceFilter.anyCondition`
  )
  locale.operations = {}
  locale.operations.list = getTranslationList(
    ['eq', 'not_eq', 'null', 'not_null'],
    `${tablePrefix}.table.filters.operations.list`
  )
  locale.operations.tagsList = getTranslationList(
    ['eq', 'not_eq', 'null', 'not_null'],
    `${tablePrefix}.table.filters.operations.tagsList`
  )
  locale.operations.string = getTranslationList(
    ['eq', 'not_eq', 'cont', 'not_cont', 'start', 'end', 'null', 'not_null'],
    `${tablePrefix}.table.filters.operations.strings`
  )
  locale.operations.number = getTranslationList(
    ['eq', 'not_eq', 'gt', 'lt', 'gteq', 'lteq', 'null', 'not_null'],
    `${tablePrefix}.table.filters.operations.number`
  )
  locale.operations.date = getTranslationList(
    ['eq', 'eq_days', 'gt', 'lt', 'lteq', 'gteq', 'null', 'not_null'],
    `${tablePrefix}.table.filters.operations.date`
  )
  locale.operations.boolean = getTranslationList(
    ['true', 'false'],
    `${tablePrefix}.table.filters.operations.boolean`
  )
  locale.groupConditions = getTranslationList(
    ['and', 'or'],
    `${tablePrefix}.table.filters.groupConditions`
  )
  locale.activeFilterViewer = {}
  locale.activeFilterViewer.simple = getTranslationList(
    ['title', 'clear'],
    `${tablePrefix}.table.filters.activeFilterViewer.simple`
  )
  locale.activeFilterViewer.advance = getTranslationList(
    ['clear'],
    `${tablePrefix}.table.filters.activeFilterViewer.advance`
  )
  return locale
}
